import type { PageMark } from '@/lib/npc-playback';

/**
 * La ligne de progression de la lecture (npc.md §11) : une règle sur la durée de la suite enregistrée,
 * marquée à chaque page, que l'on peut tirer. Les marques sont les pages ; la longue plage entre deux
 * marques est la conversation.
 *
 * Le contrôle est un `input[type=range]` natif - le clavier, les flèches et la valeur annoncée sont
 * gratuits - posé TRANSPARENT sur la piste : la règle, la part jouée, la poignée et les marques sont
 * dessinées dessous, hors de portée du pointeur, et le cadre de focus est celui du groupe.
 */

export interface PlaybackProgressProps {
  at: number;
  length: number;
  marks: readonly PageMark[];
  onSeek: (at: number) => void;
}

export function PlaybackProgress({ at, length, marks, onSeek }: PlaybackProgressProps) {
  const percent = (value: number): string =>
    `${length === 0 ? 0 : Math.min(100, (value / length) * 100)}%`;
  return (
    <div className="relative h-6 min-w-32 flex-1 rounded-sm focus-within:ring-2 focus-within:ring-bleu-nuit">
      <div
        aria-hidden="true"
        className="-translate-y-1/2 absolute inset-x-0 top-1/2 h-0.5 bg-white/30"
      />
      <div
        aria-hidden="true"
        className="-translate-y-1/2 absolute top-1/2 left-0 h-0.5 bg-white"
        style={{ width: percent(at) }}
      />
      {marks.map((mark) => (
        <span
          key={`${mark.rank}-${mark.at}`}
          aria-hidden="true"
          data-testid="regarder-marque"
          className="-translate-y-1/2 pointer-events-none absolute top-1/2 h-3 w-px bg-white/70"
          style={{ left: percent(mark.at) }}
        />
      ))}
      <span
        aria-hidden="true"
        className="-translate-x-1/2 -translate-y-1/2 pointer-events-none absolute top-1/2 size-2.5 rounded-full bg-white"
        style={{ left: percent(at) }}
      />
      <input
        type="range"
        data-testid="regarder-progression"
        aria-label="Progression de la lecture"
        min={0}
        max={length}
        step={50}
        value={Math.min(at, length)}
        onChange={(event) => onSeek(Number(event.currentTarget.value))}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      />
    </div>
  );
}
